import React, { useCallback, useState } from 'react'
import { Grid } from '@material-ui/core'
import { useHistory } from 'react-router-dom'
import AdminPage from '../../../../admin/shared/AdminPage'
import Loading from '../../../../../molecules/Loading'
import SydButton from '../../../../../commonDesign/Button'
import SydTabs from '../../../../../commonDesign/SydTabs'
import FlexRow from '../../../../../molecules/FlexRow'
import { useDenaliContext } from '../../../DenaliProvider'
import { decipherError } from '../../../../../../utils/decipherError'
import { useDataMartForm } from './useDataMartForm'
import ConfigurationTab from './ConfigurationTab'
import RunsTab from './RunsTab'
import DataMartStatusBadge from './DataMartStatusBadge'

const allTabs = {
  configuration: { label: 'Configuration', value: 'configuration' },
  runs: { label: 'Runs', value: 'runs' }
}

const newOnlyTabs = {
  configuration: { label: 'Configuration', value: 'configuration' }
}

function EditDataMart () {
  const history = useHistory()
  const { setAlert } = useDenaliContext()
  const [currentTab, setCurrentTab] = useState('configuration')
  const [saveErrors, setSaveErrors] = useState(null)
  const {
    form, isNew, isLoading, isSaving, isReconciling, reconcileResult,
    pipelineList, pipelineDetail,
    setField, setConfig, setTableConfig, setBehavior, setWorkflowType,
    deriveColumnsFromPipeline, resetMigrationHash, save, reconcile,
    WORKFLOW_TYPES, RUN_BY_OPTIONS, PERIOD_OPTIONS, DATA_TYPES
  } = useDataMartForm()

  const handleSave = useCallback(async () => {
    try {
      setSaveErrors(null)
      await save()
      history.push('/admin/denali/analysis/data-marts')
    } catch (err) {
      console.error('Failed to save data mart', err)
      setAlert({ severity: 'error', alertMessage: decipherError(err) })
      const validationErrors = err?.response?.data
      if (Array.isArray(validationErrors) && validationErrors.length > 0) {
        setSaveErrors(validationErrors)
      }
    }
  }, [save, history, setAlert])

  const handleReconcile = useCallback(async () => {
    return reconcile(true)
  }, [reconcile])

  if (isLoading) {
    return <AdminPage><Loading /></AdminPage>
  }

  const tabs = isNew ? newOnlyTabs : allTabs

  return (
    <AdminPage>
      <SydTabs.Group
        tabOptions={tabs}
        onChange={setCurrentTab}
        defaultValue={currentTab}
        key='data_mart_detail_tabs'
      >
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <FlexRow style={{ justifyContent: 'space-between', alignItems: 'center' }}>
              <FlexRow style={{ gap: '12px', alignItems: 'center' }}>
                <h2>{isNew ? 'New Data Mart' : `Data Mart: ${form.name}`}</h2>
                {!isNew && <DataMartStatusBadge timeSeriesId={form.timeSeriesId} variant='full' />}
              </FlexRow>
              <FlexRow style={{ gap: '8px', alignItems: 'center' }}>
                <SydTabs.MiniList />
                <SydButton
                  variant='outline'
                  size='sm'
                  onClick={() => history.push('/admin/denali/analysis/data-marts')}
                >
                  Cancel
                </SydButton>
                <SydButton
                  variant='primary'
                  size='sm'
                  onClick={handleSave}
                  disabled={isSaving || !form.name || !form.configuration.sourcePipelineId}
                >
                  {isSaving ? 'Saving...' : 'Save'}
                </SydButton>
              </FlexRow>
            </FlexRow>
          </Grid>

          {saveErrors && (
            <Grid item xs={12}>
              <div
                style={{
                  padding: '12px',
                  border: '1px solid var(--color-danger-500)',
                  borderRadius: 'var(--border-radius-sm)',
                  backgroundColor: 'color-mix(in srgb, var(--color-danger-100) 20%, transparent)'
                }}
              >
                <div style={{ fontWeight: 600, color: 'var(--color-danger-500)', marginBottom: '6px' }}>
                  Validation errors:
                </div>
                <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '0.8125rem' }}>
                  {saveErrors.map((e, i) => (
                    <li key={i} style={{ color: 'var(--color-danger-500)' }}>
                      {e.message}
                    </li>
                  ))}
                </ul>
              </div>
            </Grid>
          )}

          <Grid item xs={12}>
            <SydTabs.Tab value='configuration'>
              <ConfigurationTab
                form={form}
                isNew={isNew}
                isSaving={isSaving}
                isReconciling={isReconciling}
                reconcileResult={reconcileResult}
                pipelineList={pipelineList}
                pipelineDetail={pipelineDetail}
                setField={setField}
                setConfig={setConfig}
                setTableConfig={setTableConfig}
                setBehavior={setBehavior}
                setWorkflowType={setWorkflowType}
                deriveColumnsFromPipeline={deriveColumnsFromPipeline}
                resetMigrationHash={resetMigrationHash}
                onSave={handleSave}
                onReconcile={handleReconcile}
                WORKFLOW_TYPES={WORKFLOW_TYPES}
                RUN_BY_OPTIONS={RUN_BY_OPTIONS}
                PERIOD_OPTIONS={PERIOD_OPTIONS}
                DATA_TYPES={DATA_TYPES}
              />
            </SydTabs.Tab>

            {!isNew && (
              <SydTabs.Tab value='runs'>
                <RunsTab
                  timeSeriesId={form.timeSeriesId}
                  enabled={form.enabled}
                />
              </SydTabs.Tab>
            )}
          </Grid>
        </Grid>
      </SydTabs.Group>
    </AdminPage>
  )
}

export default EditDataMart
